import { Form, Switch, Input, InputNumber, Select, Alert } from 'antd'
import type { FeatureDefinition } from '@/types/feature-definition'

const { TextArea } = Input

interface FeatureValueEditorProps {
  feature: FeatureDefinition
  value?: unknown
  onChange?: (value: unknown) => void
}

/**
 * Dynamic input component that adapts to feature data type
 */
export const FeatureValueEditor = ({ feature, value, onChange }: FeatureValueEditorProps) => {
  const handleChange = (newValue: unknown) => {
    if (onChange) {
      onChange(newValue)
    }
  }

  // Parse UI config if available
  const uiConfig = feature.ui_config as
    | {
        inputType?: string
        helpText?: string
        placeholder?: string
        min?: number
        max?: number
        options?: Array<{ label: string; value: string | number }>
      }
    | undefined

  const helpText = uiConfig?.helpText || feature.description

  switch (feature.data_type) {
    case 'boolean':
      return (
        <Form.Item
          name="value"
          label={feature.name}
          help={helpText}
          valuePropName="checked"
          initialValue={value !== undefined ? value : feature.default_value}
        >
          <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" onChange={handleChange} />
        </Form.Item>
      )

    case 'string':
      if (uiConfig?.inputType === 'select' && uiConfig.options) {
        return (
          <Form.Item
            name="value"
            label={feature.name}
            help={helpText}
            rules={[{ required: true, message: `${feature.name} is required` }]}
            initialValue={value !== undefined ? value : feature.default_value}
          >
            <Select
              placeholder={uiConfig.placeholder || `Select ${feature.name}`}
              options={uiConfig.options}
              onChange={handleChange}
            />
          </Form.Item>
        )
      }

      return (
        <Form.Item
          name="value"
          label={feature.name}
          help={helpText}
          rules={[{ required: true, message: `${feature.name} is required` }]}
          initialValue={value !== undefined ? value : feature.default_value}
        >
          <Input
            placeholder={uiConfig?.placeholder || `Enter ${feature.name}`}
            onChange={(e) => handleChange(e.target.value)}
          />
        </Form.Item>
      )

    case 'number':
      return (
        <Form.Item
          name="value"
          label={feature.name}
          help={helpText}
          rules={[{ required: true, message: `${feature.name} is required` }]}
          initialValue={value !== undefined ? value : feature.default_value}
        >
          <InputNumber
            style={{ width: '100%' }}
            min={uiConfig?.min}
            max={uiConfig?.max}
            placeholder={uiConfig?.placeholder || `Enter ${feature.name}`}
            onChange={handleChange}
          />
        </Form.Item>
      )

    case 'json':
      return (
        <>
          <Form.Item
            name="value"
            label={feature.name}
            help={helpText}
            rules={[
              { required: true, message: `${feature.name} is required` },
              {
                validator: async (_, val) => {
                  if (!val) return
                  try {
                    JSON.parse(val as string)
                  } catch (_e) {
                    throw new Error('Invalid JSON format')
                  }
                },
              },
            ]}
            initialValue={
              value !== undefined
                ? typeof value === 'string'
                  ? value
                  : JSON.stringify(value, null, 2)
                : feature.default_value
                  ? typeof feature.default_value === 'string'
                    ? feature.default_value
                    : JSON.stringify(feature.default_value, null, 2)
                  : '{}'
            }
          >
            <TextArea
              rows={8}
              placeholder='{\n  "key": "value"\n}'
              style={{ fontFamily: 'monospace' }}
              onChange={(e) => handleChange(e.target.value)}
            />
          </Form.Item>
          <Alert
            type="info"
            message="JSON Format"
            description="Enter valid JSON. The value will be validated before saving."
            showIcon
            style={{ marginBottom: 16 }}
          />
        </>
      )

    default:
      return (
        <Alert
          type="error"
          message="Unsupported Data Type"
          description={`Data type "${feature.data_type}" is not supported`}
          showIcon
        />
      )
  }
}
